import { useState } from 'react';
import { parseCSV } from '../utils/csv';
import { leadsService, contactService } from '../services/api';

// Maximum CSV file size (10 MB)
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const INITIAL_PROGRESS = {
  open: false,
  total: 0,
  current: 0,
  successCount: 0,
  failedRows: [],
  done: false,
  unmatchedSources: [],
};

export function useCsvUpload(importTypeOrSources = 'leads', onImportDone = null) {
  const importType = typeof importTypeOrSources === 'string' ? importTypeOrSources : 'leads';
  const [importProgress, setImportProgress] = useState(INITIAL_PROGRESS);

  const closeImportModal = () => {
    setImportProgress(INITIAL_PROGRESS);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ── FILE SIZE GUARD ──────────────────────────────────────────────────────
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImportProgress({
        ...INITIAL_PROGRESS,
        open: true,
        done: true,
        importError: `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed is 10 MB.`,
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const rows = parseCSV(evt.target.result);
        const findHeaderIndex = (rows, type) => {
            let bestIndex = -1;
            let bestScore = -1;
            const leadKeywords = [
                'client', 'name', 'customer', 'lead', 'title',
                'phone', 'contact', 'mobile', 'cell', 'whatsapp',
                'email', 'mail',
                'status', 'stage',
                'source',
                'manager', 'assignee', 'sales',
                'inquiry', 'date', 'created',
                'event', 'function', 'venue', 'guests',
                'amount', 'quotation', 'estimate', 'budget',
                'notes', 'visited', 'follow'
            ];
            const contactKeywords = [
                'first', 'last', 'name', 'phone', 'contact', 'mobile', 'email', 'company', 'address'
            ];
            const keywords = type === 'contacts' ? contactKeywords : leadKeywords;

            const limit = Math.min(rows.length, 25);
            for (let i = 0; i < limit; i++) {
                const row = rows[i];
                if (!Array.isArray(row) || row.length < 2) continue;
                let score = 0;
                let hasNameOrPhone = false;

                for (const cell of row) {
                    if (!cell) continue;
                    const clean = String(cell).replace(/"/g, '').trim().toLowerCase();
                    if (!clean) continue;
                    if (keywords.some(kw => clean.includes(kw))) {
                        score++;
                    }
                    if (clean.includes('name') || clean.includes('client') || clean.includes('phone') || clean.includes('contact') || clean.includes('mobile')) {
                        hasNameOrPhone = true;
                    }
                }

                if (hasNameOrPhone && score > bestScore) {
                    bestScore = score;
                    bestIndex = i;
                }
            }
            return bestIndex;
        };

        const headerIndex = findHeaderIndex(rows, importType);

        if (headerIndex === -1) {
          setImportProgress({ ...INITIAL_PROGRESS, open: true, done: true, importError: 'Invalid CSV: Could not find required header.' });
          e.target.value = '';
          return;
        }

        const headers = rows[headerIndex].map((h) => h.replace(/"/g, '').trim().toLowerCase());
        const get = (row, ...keys) => {
          for (const k of keys) {
            let idx = headers.findIndex((h) => h === k);
            if (idx === -1) idx = headers.findIndex((h) => h.includes(k));
            if (idx > -1 && row[idx] !== undefined && row[idx] !== null) {
              return String(row[idx]).replace(/"/g, '').trim();
            }
          }
          return '';
        };

        const dataToImport = [];

        /**
         * KEY FIX: Excel exports comma-formatted numbers WITHOUT quotes in CSV.
         * e.g. 1,450,000 → ["1", "450", "000"] (3 separate cells).
         *
         * IMPORTANT: Only merge at the specific AMOUNT column — not at every
         * pure-digit cell — because phone numbers (e.g. 3030523775) are also
         * pure-digit and would incorrectly merge with the following amount fragment.
         */
        const normalizeRow = (row) => {
          // Find the amount column in the header
          const amtIdx = headers.findIndex(
            (h) => h === 'amount' || h === 'quotation amount' || h.startsWith('amount') || h.includes('estimate')
          );
          if (amtIdx < 0 || amtIdx >= row.length) return row;

          // Relaxed check: The first part might be "Rs 1" or just "1". 
          // We rely on the *continued* presence of 3-digit chunks to identify a split.

          // Count how many following cells are exactly-3-digit groups
          let j = amtIdx + 1;
          while (j < row.length && /^\d{3}$/.test((row[j] || '').trim())) j++;

          if (j === amtIdx + 1) return row; // nothing to merge

          // Re-build row: merge the split cells back into one amount cell
          const merged = row.slice(amtIdx, j).join('');
          return [
            ...row.slice(0, amtIdx),
            merged,
            ...row.slice(j)
          ];
        };

        const cleanNumber = (val) => {
          if (!val) return '';
          return String(val).replace(/[^\d.]/g, '');
        };

        rows.slice(headerIndex + 1).forEach((row, originalRowIndex) => {
          // If row is too short to be a valid lead, skip it.
          if (!row || row.length < 2) {
            console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Too short or null.`, row);
            return;
          }

          // Skip completely empty rows or rows that contain only empty strings and/or whitespace.
          const isRowEmpty = row.every(cell => {
            if (cell === null || cell === undefined) return true;
            // Remove all whitespace, quotes, and non-printable characters.
            // If nothing is left, the cell is effectively empty.
            return String(cell).replace(/[\s\uFEFF\xA0"']/g, '') === '';
          });
          if (isRowEmpty) {
            console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: Appears empty.`, row);
            return;
          }

          // Normalize first so split number cells don't offset subsequent columns
          const r = normalizeRow(row);

          if (importType === 'contacts') {
            const firstName = get(r, 'first name', 'firstname', 'name');
            const lastName = get(r, 'last name', 'lastname');
            const phoneStr = get(r, 'phone', 'contact', 'mobile', 'cell');
            const emailStr = get(r, 'email', 'mail');
            
            const isNameEmpty = !firstName || String(firstName).replace(/[\s\uFEFF\xA0"']/g, '') === '';
            const isPhoneEmpty = !phoneStr || String(phoneStr).replace(/[\s\uFEFF\xA0"']/g, '') === '';
            
            if (isNameEmpty && isPhoneEmpty) return;
            
            dataToImport.push({
               firstName: firstName || 'Unknown',
               lastName: lastName || null,
               phone: phoneStr,
               email: emailStr || null
            });
          } else {
            const name = get(r, 'client name', 'clientname', 'client', 'customer name', 'customer', 'lead name', 'lead', 'title', 'name', 'person');
            const phone = get(r, 'phone', 'contact number', 'mobile number', 'phone number', 'contact', 'mobile', 'cell', 'tel', 'whatsapp', 'number');

            // Strict Validation: A lead MUST have at least a Name or a Phone. If both are purely empty, it's trash data.
            const isNameEmpty = !name || String(name).replace(/[\s\uFEFF\xA0"']/g, '') === '';
            const isPhoneEmpty = !phone || String(phone).replace(/[\s\uFEFF\xA0"']/g, '') === '';

            if (isNameEmpty && isPhoneEmpty) {
              console.warn(`Skipping row ${headerIndex + 1 + originalRowIndex + 1}: No client name or phone number found.`, row);
              return; // Discard completely invalid leads that only have stray commas
            }

            const notesVal = get(r, 'notes');
            const visitedVal = get(r, 'visited');
            const followUpVal = get(r, 'date for follow-up', 'follow up', 'follow-up', 'follow up date');
            const isSiteVisited = visitedVal && visitedVal.toLowerCase().trim() === 'yes';
            
            let combinedNotes = notesVal;
            if (visitedVal) combinedNotes = combinedNotes ? `${combinedNotes} | Visited: ${visitedVal}` : `Visited: ${visitedVal}`;

            dataToImport.push({
              clientName: name || 'Unknown Client',
              amount: cleanNumber(get(r, 'quotation amount', 'amount', 'quotation', 'estimate', 'price', 'total', 'value')),
              quotationAmount: cleanNumber(get(r, 'quotation amount', 'amount', 'quotation', 'estimate', 'price', 'total', 'value')),
              clientBudget: cleanNumber(get(r, 'client budget', 'budget', 'expected budget')),
              status: (() => {
                  let rawStage = get(r, 'stage', 'status') || '';
                  const lower = rawStage.toLowerCase();
                  if (lower === 'site visited') return 'Quoted';
                  if (lower === 'qualified') return 'Contacted';
                  if (lower === 'negotiation') return 'Quoted';
                  return rawStage;
              })(),
              notes: combinedNotes,
              nextCallDate: followUpVal || null,
              phone: phone,
              email: get(r, 'email', 'mail'),
              source: get(r, 'source of lead', 'lead source', 'source', 'channel', 'medium'),
              manager: get(r, 'manager', 'handling manager', 'assignee', 'assigned to', 'assignedto', 'sales', 'salesperson', 'employee'),
              inquiryDate: get(r, 'inquiry date', 'inquirydate', 'created at', 'created', 'lead date', 'date'),
              eventDate: get(r, 'event date', 'eventdate', 'function date'),
              eventType: get(r, 'function', 'event type', 'eventtype', 'function type', 'occasion'),
              guests: get(r, 'guests', 'pax', 'headcount', 'attendees'),
              venue: get(r, 'preferred venue', 'venue', 'location'),
              siteVisited: isSiteVisited
            });
          }
        });

        if (dataToImport.length === 0) {
          setImportProgress({ ...INITIAL_PROGRESS, open: true, done: true, importError: `No valid ${importType} found in the CSV file.` });
          e.target.value = '';
          return;
        }

        // Open modal and start import
        setImportProgress({
          open: true,
          total: dataToImport.length,
          current: 0,
          successCount: 0,
          failedRows: [],
          done: false,
          unmatchedSources: [],
        });

        // Import all leads via API (which processes them server-side)
        // We split into chunks to show progress on the frontend
        const CHUNK_SIZE = 500;
        let successCount = 0;
        const failedRows = [];
        let processed = 0;

        for (let i = 0; i < dataToImport.length; i += CHUNK_SIZE) {
          const chunk = dataToImport.slice(i, i + CHUNK_SIZE);
          const chunkStartIndex = i; // CSV data row index (0-based within dataToImport)

          try {
            const result = importType === 'contacts' 
                ? await contactService.import(chunk)
                : await leadsService.import(chunk);

            // Server returns exact counts — use successCount directly (no arithmetic)
            const chunkSuccess = result.data?.successCount ?? chunk.length;
            successCount += chunkSuccess;

            // Map errors back to their absolute row numbers
            if (result.data?.errors?.length > 0) {
              result.data.errors.forEach((err, errIdx) => {
                const absRowNumber = headerIndex + 1 + chunkStartIndex + errIdx + 2;
                failedRows.push({
                  rowNumber: absRowNumber,
                  clientName: err.row || chunk[errIdx]?.clientName || chunk[errIdx]?.firstName || '—',
                  error: err.error,
                });
              });
            }
          } catch (chunkErr) {
            // Entire chunk failed
            chunk.forEach((lead, ci) => {
              const absRowNumber = headerIndex + 1 + chunkStartIndex + ci + 2;
              failedRows.push({
                rowNumber: absRowNumber,
                clientName: lead.clientName || lead.firstName,
                error: chunkErr.message,
              });
            });
          }

          processed += chunk.length;

          // Update progress in state
          setImportProgress(prev => ({
            ...prev,
            current: processed,
            successCount,
            failedRows: [...failedRows],
          }));
        }

        // Mark done
        setImportProgress(prev => ({
          ...prev,
          current: dataToImport.length,
          successCount,
          failedRows: [...failedRows],
          done: true,
        }));

        // Refresh leads data without page reload
        if (typeof onImportDone === 'function') {
          onImportDone();
        }

      } catch (error) {
        console.error('Import failed:', error);
        setImportProgress(prev => ({
          ...prev,
          done: true,
          failedRows: [...prev.failedRows, { rowNumber: '?', clientName: 'Unknown', error: error.message }],
        }));
      } finally {
        // Reset file input so the same file can be re-selected
        e.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  return { importProgress, closeImportModal, handleFileUpload };
}
